import { format, parse } from 'date-fns';
import { RiskItem } from 'src/app/api';
import { DeclarationState } from 'src/app/core/declaration.store';

import { ChildInfo } from 'src/app/core/offer.store';
import { RPM } from 'src/app/shared/constants/movable-heritage.constants';
import { OBJVAL } from 'src/app/shared/constants/object-value.constants';
import {
  Lessor,
  LessorType,
  QualiteOccupant,
  SuperficieChambre,
  SurfaceDependance,
  TypeHabitation,
} from 'src/app/shared/enum/housing.enum';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import {
  ADR_ETAGE,
  FRANCHISE,
  HAB_TYPE,
  NATURE_LOG,
  NATURE_OCCU,
  NBR_COLOC,
  NBR_PIECES,
  NBR_PIECES_40M2,
  NOM_BAILLEUR,
  POSSEDE_PISCINE,
  TYPE_BAILLEUR,
  TYPE_CH,
} from 'src/app/shared/models/model/concretes/shared/risk-item-values-ani.model';
import { DeductibleType } from '../enum/deductible-type.enum';
import { YearHouseBuilded } from '../enum/house-builded.enum';
import { QuotationSummaryResponse } from '../models/model/concretes/shared/quotation-summary.model';

// Fonction qui génère le RiskItems avec `store` passé en paramètre
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRiskItems(store: any): RiskItem[] {
  const declaration = store.declaration() as DeclarationState;
  const path = store.path() as string;
  const climatic =
    store.climatic().ClimaticPeril.length > 0 ? JSON.stringify(store.climatic().ClimaticPeril) : '';
  const arrayOfRiskItems: RiskItem[] = [];

  console.log('risk items', declaration);
  const riskItemHAB = {
    order: 1,
    typeCode: 'HAB',
    name: 'Habitation',
    fields: {
      ADR_RISQUE:
        declaration.address?.addressAutocomplete?.streetNumber &&
        declaration.address?.addressAutocomplete.street
          ? `${declaration.address.addressAutocomplete.streetNumber} ${declaration.address.addressAutocomplete.street}`
          : (declaration.address?.address ?? ''),
      ADR_CODE_POSTAL:
        declaration.address?.addressAutocomplete?.zipCode ?? declaration.address?.zipCode ?? '',
      ADR_COMP: '',
      ADR_VILLE: declaration.address?.addressAutocomplete?.city ?? declaration.address?.city ?? '',
      ADR_ETAGE: declaration.apartmentFloor ?? '',
      HAB_TYPE: declaration.housingType ?? TypeHabitation.Appartement,
      NBR_PIECES_40M2: declaration.numberOfRooms?.between41And75 ?? 0,
      NATURE_LOG: declaration.residenceType ?? '',
      NATURE_OCCU: declaration.qualiteOccupant ?? '',
      NBR_PIECES: declaration.numberOfRooms?.numberOfRooms ?? 0,
      SURFACE_DEP: declaration.dependances?.otherDependanceSurface ?? SurfaceDependance.Aucune,
      POSSEDE_ENERGIEV: declaration.durableDevelopmentInstallation ?? '',
      POSSEDE_VERANDA: declaration.numberOfRooms?.hasVeranda ?? false,
      POSSEDE_PISCINE: declaration.hasSwimmingPoolSpaOrJacuzzi ?? false,
      REV_FISCAL: declaration.revenus ?? '',
      TYPE_CH: declaration.typeOfBedroom ?? SuperficieChambre.Unknown,
      NBR_COLOC: declaration.nbColoc ?? 0,
      NOM_BAILLEUR: '', // defined below
      CAP_MOB_BESOIN: getCapitalMobilierFromRiskItems(path, declaration) ?? '',
      OBJ_VAL_UNIT: !!declaration.capitalMobilier?.valuableObjects,
      CLIMATIC_PERILS: climatic,
      NBR_PIECES_76M2: declaration.numberOfRooms?.moreThan76 ?? 0,
      POSSEDE_GARAGE: declaration.dependances?.hasGarage ?? null,
      POSSEDE_CAVE_9M2: !!declaration.dependances?.hasBasement,
      OBJVAL: declaration.objetValue ?? '',
      RPM: declaration.movableHeritage ?? '',
      ANNEE_CONSTRUCTION: declaration.dateHouseBuilted ?? YearHouseBuilded.Inconnue,
      TYPE_BAILLEUR:
        declaration.qualiteOccupant === QualiteOccupant.Locataire
          ? (declaration.lessorType ?? Lessor.Prive)
          : Lessor.Unknown,
      FRANCHISE:
        store.path() === PathType.CONFORT
          ? (declaration.deductibleType ?? DeductibleType.TroisCents)
          : DeductibleType.CentCinquante,
    },
  };

  if (declaration.lessor && declaration.lessor.id !== LessorType.Other) {
    riskItemHAB.fields.NOM_BAILLEUR = declaration.lessor.name ?? '';
  }

  arrayOfRiskItems.push(riskItemHAB);
  const children = store.offer().options.schoolInsurance.children;
  children
    .filter((child: ChildInfo) => child.lastName && child.firstName && child.birthDate)
    .forEach((child: ChildInfo, index: number) => {
      const childRiskItem = {
        name: 'Enfant',
        typeCode: 'ENF',
        order: index + 2, // L'ordre commence à 2 car l'habitation est à 1
        fields: {
          NOM_ENFANT: child.lastName ?? '',
          PRENOM_ENFANT: child.firstName ?? '',
          DATE_ENFANT: child.birthDate ? format(child.birthDate, 'dd/MM/yyyy') : '',
        },
      };
      arrayOfRiskItems.push(childRiskItem);
    });

  return arrayOfRiskItems;
}

// Fonction qui génère le DeclarationState avec `QuotationSummaryResponse` passé en paramètre
function getDeclarationStateFromQuotationSummary(
  quotationSummary: QuotationSummaryResponse,
): DeclarationState {
  return {
    // name: quotationSummary.riskItemInformations.values.find(v => v.code === NOM_ANI)?.value ?? '',
    email: '',
    address: {
      addressAutocomplete: {
        streetNumber:
          quotationSummary.clientInformations.address1.split(' ').slice(0, 1).join(' ') ?? '',
        street: quotationSummary.clientInformations.address1.split(' ')[0] ?? '',
        zipCode: quotationSummary.clientInformations.zipCode,
        city: quotationSummary.clientInformations.addressCity,
      },
      address: quotationSummary.clientInformations.address1,
      city: quotationSummary.clientInformations.addressCity,
      country: null,
      zipCode: quotationSummary.clientInformations.zipCode,
    },
    residenceType:
      quotationSummary.riskItemInformations.values.find((v) => v.code === NATURE_LOG)?.value ??
      null,
    housingType:
      quotationSummary.riskItemInformations.values.find((v) => v.code === HAB_TYPE)?.value ?? null,
    dateHouseBuilted: null,
    apartmentFloor:
      quotationSummary.riskItemInformations.values.find((v) => v.code === ADR_ETAGE)?.value ?? null,
    qualiteOccupant:
      quotationSummary.riskItemInformations.values.find((v) => v.code === NATURE_OCCU)?.value ??
      null,
    nbColoc: Number(
      quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_COLOC)?.value ?? 0,
    ),
    deductibleType:
      quotationSummary.formulasAndOptionsInformations.formulaCode === PathType.CONFORT
        ? (quotationSummary.riskItemInformations.values.find((v) => v.code === FRANCHISE)?.value ??
          DeductibleType.TroisCents)
        : DeductibleType.CentCinquante,
    nbPersonInHome: null,
    colocInformations: null,
    typeOfBedroom:
      quotationSummary.riskItemInformations.values.find((v) => v.code === TYPE_CH)?.value ??
      SuperficieChambre.Unknown,
    lessor: quotationSummary.riskItemInformations.values.find((v) => v.code === NOM_BAILLEUR)?.value
      ? {
          id: -2, // Assuming social if the lessor is not "Other"
          name:
            quotationSummary.riskItemInformations.values.find((v) => v.code === NOM_BAILLEUR)
              ?.value ?? '',
          lessorType: 0,
          email: '',
        }
      : {
          id: -2, // Assuming social if the lessor is not "Other"
          name: 'Autre',
          lessorType: 0,
          email: '',
        },
    lessorType:
      quotationSummary.riskItemInformations.values.find((v) => v.code === TYPE_BAILLEUR)?.value ??
      Lessor.Social,
    subscriberHasBeenCanceled: null,
    susbcriberHadClaimsInLast3Years: null,
    numberOfRooms: {
      numberOfRooms: Number(
        quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_PIECES)?.value ?? 0,
      ),
      between41And75: Number(
        quotationSummary.riskItemInformations.values.find((v) => v.code === NBR_PIECES_40M2)
          ?.value ?? 0,
      ),
      moreThan76: null,
      hasVeranda: null,
    },
    dependances: null,
    durableDevelopmentInstallation: null,
    hasSwimmingPoolSpaOrJacuzzi: !!quotationSummary.riskItemInformations.values.find(
      (v) => v.code === POSSEDE_PISCINE,
    )?.value,
    capitalMobilier: null,
    preferentialRate: null,
    effect_date: quotationSummary.requestedStartEffectiveDate
      ? format(
          parse(quotationSummary.requestedStartEffectiveDate, 'yyyy-MM-dd', new Date()),
          'dd/MM/yyyy HH:mm:ss',
        )
      : null,
    movableHeritage:
      quotationSummary.riskItemInformations.values.find((v) => v.code === RPM)?.value ?? '',
    objetValue:
      quotationSummary.riskItemInformations.values.find((v) => v.code === OBJVAL)?.value ?? '',
    revenus: '',
  };
}

// Fonction qui retourne un RiskItem spécifique
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// export function getRiskItem(store: any, key: string): RiskItem | undefined {
//   const riskItems = getRiskItems(store);
//   return riskItems[key]; // Retourne l'objet si trouvé, sinon `undefined`
// }

export function getDeclarationState(quotationSummary: QuotationSummaryResponse): DeclarationState {
  return getDeclarationStateFromQuotationSummary(quotationSummary);
}

// function format_date(dateString: string): string {
//   let date: Date = new Date();
//   if (typeof dateString === "string") {
//     date = parse(dateString, "dd/MM/yyyy", new Date());
//   }

//   return format(date, "yyyy-MM-dd");
// }

/// Function that should return  "<20000" if the capital mobilier is less than 20000, "Entre 20000 et 50000" if it's between 20000

function getCapitalMobilierFromRiskItems(path: string, store: DeclarationState): string | null {
  let value = '';

  if (path === PathType.ETUDIANT) {
    // HANDLE CAPITAL MOBILIER FOR ETUDIANT PATH
  } else {
    value = store.capitalMobilier?.insuredValue ?? '';
  }

  return value;
}
